import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { Mail, MessageCircle, CheckCircle2 } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";

export default function ContactPage() {
  return (
    <MarketingLayout>
      <section data-testid="contact-page" className="relative">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-14 py-24 sm:py-28">
          <div className="text-[12px] uppercase tracking-[0.28em] font-semibold text-[#B59A5A]">
            Contact
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A422D] mt-4 leading-[1.05]">
            We&rsquo;d be glad to hear from you.
          </h1>
          <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed max-w-3xl">
            Whether you&rsquo;re a member, a private club, or a vendor, reach out and someone from the team will respond when onboarding opens.
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            <ContactCard
              icon={<Mail size={22} />}
              title="Email"
              body="theshop@mycrestclub.com"
              href="mailto:theshop@mycrestclub.com"
              testId="contact-email"
            />
            <ContactCard
              icon={<MessageCircle size={22} />}
              title="Schedule a Demo"
              body="Talk to us about your club or brand"
              href="mailto:theshop@mycrestclub.com?subject=Demo%20Request"
              testId="contact-demo"
            />
          </div>

          <ClubInquiryForm />

          <div className="mt-14 text-sm text-[#7A7A7A]">
            Already a member?{" "}
            <Link to="/login" className="text-[#1A422D] underline underline-offset-4 font-semibold hover:text-[#B59A5A]">
              Sign in here.
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

function ClubInquiryForm() {
  const [form, setForm] = useState({
    club_name: "", contact_name: "", email: "", phone: "", city: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const baseURL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${baseURL}/api/public/clubs/inquiry`, form);
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="club-inquiry-success"
        className="mt-16 p-8 border border-[#1A422D] bg-[#E9F0EB]"
      >
        <div className="inline-flex items-center gap-2 text-[#1A422D]">
          <CheckCircle2 size={22} />
          <span className="text-[12px] uppercase tracking-[0.24em] font-semibold">
            You&rsquo;re on the early-access list
          </span>
        </div>
        <h3 className="font-serif text-2xl mt-3 text-[#1A422D]">
          Thank you for your interest in My Crest Club.
        </h3>
        <p className="text-sm text-[#4A4A4A] mt-3 leading-relaxed">
          Your information has been added to our early-access queue. As soon as the
          platform is ready to begin onboarding, our team will contact you directly
          with next steps. Check your inbox for a confirmation note.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="club-inquiry-form"
      className="mt-16 border border-[#E5E3D8] bg-white p-8 sm:p-10"
    >
      <div className="text-[12px] uppercase tracking-[0.24em] font-semibold text-[#B59A5A]">
        For Private Clubs
      </div>
      <h2 className="font-serif text-3xl mt-2 text-[#1A422D]">Join the early-access list</h2>
      <p className="text-sm text-[#4A4A4A] mt-3 leading-relaxed max-w-2xl">
        Tell us a little about your club. We&rsquo;ll reach out personally when we open the
        next onboarding window.
      </p>

      {error && (
        <div className="mt-5 px-4 py-3 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]">
          {error}
        </div>
      )}

      <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Club name" required testId="club-inquiry-name">
          <input
            type="text" required maxLength={200}
            value={form.club_name} onChange={update("club_name")}
            data-testid="club-inquiry-club-name"
            className="input-field"
            placeholder="e.g. Bay Hill Club & Lodge"
          />
        </Field>
        <Field label="Your name" required testId="club-inquiry-contact">
          <input
            type="text" required maxLength={200}
            value={form.contact_name} onChange={update("contact_name")}
            data-testid="club-inquiry-contact-name"
            className="input-field"
            placeholder="e.g. Head Golf Professional"
          />
        </Field>
        <Field label="Email" required>
          <input
            type="email" required maxLength={200}
            value={form.email} onChange={update("email")}
            data-testid="club-inquiry-email"
            className="input-field"
            placeholder="you@yourclub.com"
          />
        </Field>
        <Field label="Phone">
          <input
            type="tel" maxLength={40}
            value={form.phone} onChange={update("phone")}
            data-testid="club-inquiry-phone"
            className="input-field"
            placeholder="Optional"
          />
        </Field>
        <Field label="City" className="sm:col-span-2">
          <input
            type="text" maxLength={120}
            value={form.city} onChange={update("city")}
            data-testid="club-inquiry-city"
            className="input-field"
            placeholder="Optional"
          />
        </Field>
        <Field label="Anything we should know?" className="sm:col-span-2">
          <textarea
            rows={3} maxLength={2000}
            value={form.notes} onChange={update("notes")}
            data-testid="club-inquiry-notes"
            className="input-field"
            placeholder="Membership size, pro-shop setup, timing — anything that helps us prepare."
          />
        </Field>
      </div>

      <button
        type="submit"
        disabled={busy}
        data-testid="club-inquiry-submit"
        className="mt-8 inline-flex items-center gap-2 px-8 py-3.5 bg-[#1A422D] text-[#F9F8F4] text-[13px] uppercase tracking-[0.28em] font-semibold hover:bg-[#0F2A1C] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {busy ? "Submitting…" : "Join the Early-Access List"}
      </button>
    </form>
  );
}

function Field({ label, required, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[#B59A5A]">
        {label}{required && <span className="text-[#A33] ml-1">*</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function ContactCard({ icon, title, body, href, testId }) {
  return (
    <a
      href={href}
      data-testid={testId}
      className="block p-6 border border-[#E5E3D8] bg-white hover:border-[#1A422D] transition-colors group"
    >
      <div className="w-11 h-11 flex items-center justify-center bg-[#1A422D] text-[#D9C68A] group-hover:bg-[#0F2A1C] transition-colors">
        {icon}
      </div>
      <div className="mt-4 text-[12px] uppercase tracking-[0.24em] font-semibold text-[#B59A5A]">
        {title}
      </div>
      <div className="mt-1 font-serif text-2xl text-[#1A422D]">{body}</div>
    </a>
  );
}
